import { Component, Input } from '@angular/core';
import { PurchaseOrderDto, VatOption, ApplicationInfoDto, ContactDto, PurchaseOrderPartItemDto, PurchaseOrderHireItemDto, PurchaseOrderProductItemDto, DlmProductDto } from 'app/api/models';
import { PdfComponent } from 'app/core/components/pdf/pdf.component';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ThemeService } from 'app/core/services/theme/theme.service';
import { SettingsService } from 'app/api/services';
import autoTable from 'jspdf-autotable';
import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { SharedModule } from 'app/core/shared.module';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatStepperModule } from '@angular/material/stepper';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { NgxPrintModule } from 'ngx-print';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'purchase-order-preview',
  templateUrl: './purchase-order-preview.component.html',
    standalone: true,
    imports: [
          CommonModule,
          SharedModule,
          MatSidenavModule,
          MatButtonModule,
          CdkScrollable,
          MatPaginatorModule,
          MatIconModule,
          MatFormFieldModule,
          MatInputModule,
          MatSelectModule,
          MatOptionModule,
          FormsModule,
          ReactiveFormsModule,
          MatStepperModule,
          CurrencyPipe,
          FormsModule,
          MatDatepickerModule,
          MatAutocompleteModule,
          NgxPrintModule,
          MatSlideToggleModule,
          MatTooltipModule,
          MatCheckboxModule,
    ],
})
export class PurchaseOrderPreviewComponent extends PdfComponent
{
  @Input() purchaseOrder: PurchaseOrderDto;
  @Input() hireItems: PurchaseOrderHireItemDto[];
  @Input() partItems: PurchaseOrderPartItemDto[];
  @Input() productItems: PurchaseOrderProductItemDto[];
  // @Input() project: PurchaseOrderBuilderProjectDto;
  @Input() contact: ContactDto;
  @Input() printSectionId: string;
  @Input() products: DlmProductDto[] = [];

  appInfo: ApplicationInfoDto;
  
  public GetPOTitle = () => `${this.purchaseOrder.salesOrderRef}-${(this.purchaseOrder.isSupplier ? this.purchaseOrder.supplier : this.purchaseOrder.company).trim().substring(0, 16).trim()}-${this.purchaseOrder.title}.pdf`;
  
  constructor(
    public themeService: ThemeService,
    private settingsService: SettingsService,
    private decimalPipe: DecimalPipe,
    private datePipe: DatePipe,
    private currencyPipe: CurrencyPipe,
  )
  {
    super();
  }

  ngOnInit(): void {
      this.loadAppInfo();
  }

  loadAppInfo() {
      this.settingsService.apiSettingsAppInfoGet$Json$Response().subscribe(res => {
          if(!res?.body?.data) return;

          this.appInfo = res.body.data;
      });
  }

  getProductDisplayName(item: PurchaseOrderProductItemDto): string {
    if (item?.productId && this.products?.length) {
      var product = this.products.find(p => p.dlmProductId === item.productId);
      if (product?.description) return product.description;
    }
    return item?.description ?? '';
  }

  getVatText(vatOption) {
    switch (vatOption) {
        case VatOption.Reduced:
            return "5%";
        case VatOption.Standard:
            return "20%";
        case VatOption.Zero:
        default:
            return "0%";
    }
  }

  getVatValue(vatOption: VatOption){
      switch(vatOption){
          case VatOption.Reduced:
          return 0.05;
          case VatOption.Standard:
          return 0.2;
          case VatOption.Zero:
          default:
          return 0;
      }
  }

  // calculations
  getItemSubtotal(purchaseOrderItem: PurchaseOrderHireItemDto | PurchaseOrderPartItemDto, includeVat: boolean = false) {
    var price = purchaseOrderItem?.price;
    var quantity = purchaseOrderItem?.quantity;
    var vat = purchaseOrderItem?.vat;
    
    return (price * quantity) * ((includeVat ? this.getVatValue(vat) : 0) + 1);
  }

  getTotalPrice() {
    var subtotal = 0;

    this.hireItems?.forEach((item: PurchaseOrderHireItemDto) => {
        subtotal += (item?.price * item?.quantity);
    });
    this.partItems?.forEach((item: PurchaseOrderPartItemDto) => {
        subtotal += (item?.price * item?.quantity);
    });
    this.productItems?.forEach((item: PurchaseOrderProductItemDto) => {
        subtotal += (item?.price * item?.quantity);
    });

    return subtotal;
  }

  getTotalVat() {
    var subtotal = 0;

    this.hireItems?.forEach((item: PurchaseOrderHireItemDto) => {
        subtotal += ((item?.price * item?.quantity) * this.getVatValue(item?.vat));
    });
    this.partItems?.forEach((item: PurchaseOrderPartItemDto) => {
        subtotal += ((item?.price * item?.quantity) * this.getVatValue(item?.vat));
    });
    this.productItems?.forEach((item: PurchaseOrderProductItemDto) => {
        subtotal += ((item?.price * item?.quantity) * this.getVatValue(item?.vat));
    });

    return subtotal;
  }

  getGrandTotal(){
    var price = this.getTotalPrice();
    var vat = this.getTotalVat();
    var freight = this.purchaseOrder?.freightAmount ?? 0;

    return price + vat + freight;
  }

  saveAsPdf() {
    this.generatePdf().then(PDF => {
        PDF.save(this.GetPOTitle());
    });
  }

  outputAsBlob() {
      return this.generatePdf().then(PDF => {
          return PDF.output('blob');
      });
  }

  generatePdf(): Promise<jsPDF> {
      let PDF = new jsPDF("p", "mm", "a4", true);

      return this.createCanvasFromElement("print-header-" + this.printSectionId).then((canvas) => {
          this.addCanvasToPdf(canvas, PDF, 9);
    
          //Title
          var dataTitle = [
            [this.purchaseOrder?.isProduct ? 'SALES ORDER' : this.purchaseOrder?.isSupplier ? 'PURCHASE ORDER' : 'HIRE ORDER']
          ];
          this.addTextToPdf(PDF, dataTitle, 19, null, 10);
          this.addTextToPdf(PDF, [['ACKNOWLEDGEMENT']], 19, null, 18);
    
          //Header
          var columnsHeader = [
              {name: ''},
              {name: ''},
          ];
          var dataHeader = [
              ['DATE', this.datePipe.transform(this.purchaseOrder?.date, 'mediumDate')],
              [`${this.purchaseOrder?.isSupplier ? 'PO' : 'SO'} Number`, this.purchaseOrder?.salesOrderRef || `NEX-ORD-${this.decimalPipe.transform(this.purchaseOrder?.purchaseOrderId, '4.0')?.replace(',', '') ?? '####'}`],
              [this.purchaseOrder?.salesOrderRef2 ? `${this.purchaseOrder?.isSupplier ? 'SO' : 'PO'} NUMBER` : '', this.purchaseOrder?.salesOrderRef2 || ''],
              ['TOTAL DUE', this.currencyPipe.transform(this.getGrandTotal(), 'GBP')],
          ];
          var optionsHeader = this.setTableOptions(
              PDF,
              ((PDF as any).lastAutoTable.finalY || 15) - 3,
              columnsHeader,
              dataHeader,
              {
              // specific column styling
              1: { cellWidth: 150 },
              },
          );
          optionsHeader.styles = {
              fontSize: 9,
              cellPadding: 1,
          },
          optionsHeader.willDrawCell = this.getBlankCellHook(PDF);
          autoTable(PDF, optionsHeader);
    
    
          //blackbox
          var columnsBox = [
            {name: ''},
          ];
          var dataBox = [];
          if(this.appInfo?.companyName) dataBox.push([this.appInfo.companyName]);
          if(this.appInfo?.companyAddressOne) dataBox.push([this.appInfo.companyAddressOne]);
          if(this.appInfo?.companyAddressTwo) dataBox.push([this.appInfo.companyAddressTwo]);
          if(this.appInfo?.companyCity) dataBox.push([this.appInfo.companyCity]);
          if(this.appInfo?.companyCounty) dataBox.push([this.appInfo.companyCounty]);
          if(this.appInfo?.companyPostcode) dataBox.push([this.appInfo.companyPostcode]);
          if(this.appInfo?.companyCountry) dataBox.push([this.appInfo.companyCountry]);

          var optionsBox = this.setTableOptions(
              PDF,
              18,
              columnsBox,
              dataBox,
          );
          optionsBox.styles = {
              fontSize: 8,
              textColor: '#ffffff',
              cellPadding: {
                left: 138,
                top: 0.5,
                bottom: 0.5,
                right: 0
              },
              
          },
          optionsBox.willDrawCell = this.getBlankCellHook(PDF);
          autoTable(PDF, optionsBox);
    

          //address
          var dataCompany = [
            [this.contact?.name ?? ''],
          ]
          this.addTextToPdf(PDF, dataCompany, 12, null, ((PDF as any).lastAutoTable.finalY || 15) + 10);

          var columnsAddress = [
            {name: ''},
            {name: ''},
          ];
          var dataAddress = [
            [this.purchaseOrder?.addressOne, ''],
            [this.purchaseOrder?.addressTwo, ''],
            [this.purchaseOrder?.city, ''],
            [this.purchaseOrder?.postcode, ''],
          ].filter(x => x[0]);

          while(dataAddress.length < 2)
            dataAddress.push(['', '']);

          var optionsAddress = this.setTableOptions(
            PDF,
            ((PDF as any).lastAutoTable.finalY || 15) - 4,
            columnsAddress,
            dataAddress,
            {
            // specific column styling
            1: { halign: 'right' },
            },
          );
          optionsAddress.styles = {
              fontSize: 9,
              cellPadding: 0.5,
          },
          optionsAddress.willDrawCell = this.getBlankCellHook(PDF);
          autoTable(PDF, optionsAddress);
    

          //items
          if(!this.purchaseOrder?.isSupplier && !this.purchaseOrder.isProduct) {
            var columnsItems = [
              {name: 'DESCRIPTION'},
              {name: 'PRICE/DAY'},
              {name: 'HIRE DATES'},
              {name: 'DAYS'},
              {name: 'VAT'},
              {name: 'TOTAL'},
            ];
            var dataItems = this.hireItems.map(item => [
              item.description,
              this.currencyPipe.transform(item?.price ?? 0, 'GBP'),
              `${this.datePipe.transform(item?.hireFrom, 'dd/MM/yy')} - ${this.datePipe.transform(item?.hireTo, 'dd/MM/yy')}`,
              item.quantity ?? 0,
              this.getVatText(item?.vat),
              this.currencyPipe.transform(this.getItemSubtotal(item) ?? 0, 'GBP')
            ]);
            
            var optionsItems = this.setTableOptions(
              PDF,
              ((PDF as any).lastAutoTable.finalY || 15) + 10,
              columnsItems,
              dataItems,
              {
              // specific column styling
              1: { halign: 'right' },
              2: { halign: 'right' },
              3: { halign: 'right' },
              4: { halign: 'right' },
              5: { halign: 'right' },
              },
            );
            optionsItems.styles = {
              fontSize: 10,
              cellPadding: 2,
            },
            //todo make all but description halign right
            autoTable(PDF, optionsItems);
          }
          else if (this.purchaseOrder.isSupplier) {
            var columnsItems = [
              {name: 'DESCRIPTION'},
              {name: 'PRICE/ITEM'},
              {name: 'QTY'},
              {name: 'VAT'},
              {name: 'TOTAL'},
            ];
            var dataItems = this.partItems.map(item => [
              item.description,
              this.currencyPipe.transform(item?.price ?? 0, 'GBP'),
              item.quantity ?? 0,
              this.getVatText(item?.vat),
              this.currencyPipe.transform(this.getItemSubtotal(item) ?? 0, 'GBP')
            ]);
            
            var optionsItems = this.setTableOptions(
              PDF,
              ((PDF as any).lastAutoTable.finalY || 15) + 10,
              columnsItems,
              dataItems,
              {
              // specific column styling
              1: { halign: 'right' },
              2: { halign: 'right' },
              4: { halign: 'right' },
              5: { halign: 'right' },
              },
            );
            optionsItems.styles = {
              fontSize: 10,
              cellPadding: 2,
            },
            //todo make all but description halign right
            autoTable(PDF, optionsItems);
          }
          else if (this.purchaseOrder.isProduct) {
            var columnsItems = [
              {name: 'DESCRIPTION'},
              {name: 'PRICE/ITEM'},
              {name: 'QTY'},
              {name: 'VAT'},
              {name: 'TOTAL'},
            ];
            var dataItems = this.productItems.map(item => [
              this.getProductDisplayName(item),
              this.currencyPipe.transform(item?.price ?? 0, 'GBP'),
              item.quantity ?? 0,
              this.getVatText(item?.vat),
              this.currencyPipe.transform(this.getItemSubtotal(item) ?? 0, 'GBP')
            ]);
            
            var optionsItems = this.setTableOptions(
              PDF,
              ((PDF as any).lastAutoTable.finalY || 15) + 10,
              columnsItems,
              dataItems,
              {
              // specific column styling
              1: { halign: 'right' },
              2: { halign: 'right' },
              4: { halign: 'right' },
              5: { halign: 'right' },
              },
            );
            optionsItems.styles = {
              fontSize: 10,
              cellPadding: 2,
            },
            //todo make all but description halign right
            autoTable(PDF, optionsItems);
          }
    
          //subtotal+total
          var columnsTotal = [
              {name: 'SUBTOTAL'},
              {name: this.currencyPipe.transform(this.getTotalPrice(), 'GBP')},
          ];
          var dataTotal = [
            ['TAX', this.currencyPipe.transform(this.getTotalVat(), 'GBP')],
            ['FREIGHT', this.currencyPipe.transform(this.purchaseOrder.freightAmount, 'GBP')],
          ];

          dataTotal.push(
            ['TOTAL', this.currencyPipe.transform(this.getGrandTotal(), 'GBP')]
          );
          var optionsTotal = this.setTableOptions(
              PDF,
              ((PDF as any).lastAutoTable.finalY || 15) + 10,
              columnsTotal,
              dataTotal,
              {
              // specific column styling
              1: { halign: 'right' },
              },
          );
          optionsTotal.styles = {
            fontSize: 10,
            cellPadding: 2,
          },
          optionsTotal.didDrawCell = function (hookData) {
            if (hookData.section === 'body') {
              if (hookData.row.index === dataTotal.length - 1) {
                for (const cell of Object.values(hookData.row.cells)) {
                    // cell.styles.fontStyle = 'bold';
                    cell.styles.fontSize = 12;
                }
              }
            }
          },
          autoTable(PDF, optionsTotal);

          return PDF;

          //payment terms
          var dataPayable1 = [
            ['Please make bank transfers payable to:'],
          ];
          this.addTextToPdf(PDF, dataPayable1, null, null, null, null);
    
          var dataPayable2 = [
              ['Elevated Software Limited'],
              ['Bank Name'],
              ['Bank Address 1, Bank Address 2, Dorset, BH1 1AA'],
          ];
          
          if(dataPayable2)
            this.addTextToPdf(PDF, dataPayable2, null, null, null, 0);


          return this.createCanvasFromElement("print-footer-" + this.printSectionId).then((canvas) => {
            this.addCanvasToPdf(canvas, PDF, ((PDF as any).lastAutoTable.finalY || 15));
    
            //footer text 2
            var columnsFooter3 = [
                {name: ''},
            ];
            var dataFooter3: string[][];
            
            dataFooter3 = [
              ['Account Number: 12345678'],
              ['Sort Code: 11-22-33'],
              ['IBAN: XXXXXXXXXXXXXXXXXXXXXX'],
              ['BIC: XXXXXXXX'],
            ];

            var optionsFooter3 = this.setTableOptions(
                PDF,
                (((PDF as any).lastAutoTable.finalY || 15) + 2),
                columnsFooter3,
                dataFooter3,
            );
            optionsFooter3.styles = {
                fontSize: 8,
                cellPadding: {
                  left: 25,
                  top: 0.5,
                  bottom: 0.5,
                  right: 0.5
                }
            },
            optionsFooter3.willDrawCell = this.getBlankCellHook(PDF);

            if(optionsFooter3)
                autoTable(PDF, optionsFooter3);
          
            var dataInvoice = [
              ['Any queries must be raised within 7 days of the date of the invoice'],
            ];
            this.addTextToPdf(PDF, dataInvoice, null, null, null, 0);
    
            return PDF;
          });
      });
  }

    
  createCanvasFromElement(elementId: string) {
      let element: any = document.getElementById(elementId);
  
      return html2canvas(element, {
        onclone: function (clonedDoc) {
            var root = clonedDoc.getElementById(elementId);
            root.style.padding = "3rem";
            // root.style.display = "flex";
            // root.style.flexDirection = 'column';
            root.style.display = 'block';
        },
        height: 1300,
      });
  }

}
